/**
 * clientErrorReporter.ts
 * ----------------------
 * frontend 런타임 오류를 backend audit.exception_logs 에 자동 보고.
 *
 * 캐치 대상:
 *  - window.error (JS runtime error, script error)
 *  - window.unhandledrejection (Promise rejection)
 *  - React ErrorBoundary (별도 컴포넌트가 reportClientError 호출)
 *
 * 안전:
 *  - 동일 stack 60초 dedup (메모리 cache)
 *  - 무한 루프 차단: report 전송 중 발생한 오류는 skip 마킹
 *  - rate limit: 분당 최대 10건
 *  - body 길이 제한
 */

const API_BASE = process.env.REACT_APP_API_URL || "https://api.trippang.com";
const APP_NAME = "frontuser";

interface ReportPayload {
  message: string;
  stack?: string;
  error_class?: string;
  page_url?: string;
  source?: string;
  line_no?: number;
  col_no?: number;
  build_version?: string;
  app?: string;
  kind?: string;
}

// 동일 오류 dedup (key: error_class|message|stack head 200)
const _seen = new Map<string, number>();
const DEDUP_WINDOW_MS = 60_000;

// 분당 rate limit (메모리)
let _windowStart = 0;
let _windowCount = 0;
const RATE_WINDOW_MS = 60_000;
const RATE_MAX = 10;

// 전송 중 플래그 (무한 루프 방지)
let _inFlight = false;

function _dedupKey(p: ReportPayload): string {
  return `${p.error_class || ""}|${(p.message || "").slice(0, 100)}|${(p.stack || "").slice(0, 200)}`;
}

function _isDuplicate(p: ReportPayload): boolean {
  const key = _dedupKey(p);
  const now = Date.now();
  const last = _seen.get(key);
  if (last && now - last < DEDUP_WINDOW_MS) return true;
  _seen.set(key, now);
  // 오래된 entry 정리 (1000건 초과 시)
  if (_seen.size > 1000) {
    const cutoff = now - DEDUP_WINDOW_MS * 2;
    _seen.forEach((t, k) => { if (t < cutoff) _seen.delete(k); });
  }
  return false;
}

function _checkRate(): boolean {
  const now = Date.now();
  if (now - _windowStart > RATE_WINDOW_MS) {
    _windowStart = now;
    _windowCount = 0;
  }
  if (_windowCount >= RATE_MAX) return false;
  _windowCount++;
  return true;
}

export async function reportClientError(p: ReportPayload): Promise<void> {
  if (_inFlight) return;
  if (_isDuplicate(p)) return;
  if (!_checkRate()) return;

  _inFlight = true;
  try {
    const body = JSON.stringify({
      ...p,
      app: p.app || APP_NAME,
      page_url: p.page_url || window.location.href,
      build_version: p.build_version || (window as any).__BUILD_VERSION__ || "",
    });
    await fetch(`${API_BASE}/public/client-errors`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      // keepalive: 페이지 unload 직전에도 전송 보장
      keepalive: true,
    });
  } catch {
    /* swallow — 보고 실패는 사용자에 영향 없음 */
  } finally {
    _inFlight = false;
  }
}

/**
 * 글로벌 핸들러 등록 — index.tsx 또는 App.tsx 진입점에서 1회 호출.
 */
export function installGlobalClientErrorReporter(): void {
  window.addEventListener("error", (ev: ErrorEvent) => {
    const err = ev.error;
    reportClientError({
      kind: "error",
      message: ev.message || String(err) || "Unknown error",
      stack: err && err.stack ? String(err.stack) : undefined,
      error_class: err && err.name ? String(err.name) : "Error",
      source: ev.filename,
      line_no: ev.lineno,
      col_no: ev.colno,
    });
  });

  window.addEventListener(
    "unhandledrejection",
    (ev: PromiseRejectionEvent) => {
      const r = ev.reason;
      let message = "";
      let stack: string | undefined;
      let cls = "UnhandledRejection";
      if (r instanceof Error) {
        message = r.message;
        stack = r.stack;
        cls = r.name || cls;
      } else if (typeof r === "string") {
        message = r;
      } else {
        try {
          message = JSON.stringify(r) || "rejection";
        } catch {
          message = String(r);
        }
      }
      reportClientError({
        kind: "unhandledrejection",
        message: message || "Promise rejection",
        stack,
        error_class: cls,
      });
    },
  );
}
